import Image from 'next/image';
import Link from 'next/link';
import Trans from 'next-translate/Trans';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  delay,
  Stage,
  tone,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import {
  Avatar,
  AvatarStack,
  COMPANY,
  COMPANY_MARK,
  COMPLIANCE,
  ShieldGlyph,
  TEAM,
  type Teammate,
  useB2bTranslation,
} from './kit';

import AnthropicSvg from '~/svg/Anthropic.svg';
import DeepseekSvg from '~/svg/deepseek.svg';
import GoogleSvg from '~/svg/google.svg';
import MetaSvg from '~/svg/meta.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import XaiSvg from '~/svg/xai.svg';
import ZaiSvg from '~/svg/zai.svg';

type Logo = ComponentType<SVGProps<SVGSVGElement>>;

/** A model shown in the hero, picked from the chat catalogue at build time. */
export type HeroModel = { provider: string; title: string };

const PROVIDER_LOGOS: Record<string, { logo: Logo; className?: string }> = {
  OpenAI: { logo: OpenAISvg, className: 'fill-[#10101c]' },
  Anthropic: { logo: AnthropicSvg },
  Google: { logo: GoogleSvg },
  xAI: { logo: XaiSvg, className: 'text-[#10101c]' },
  Deepseek: { logo: DeepseekSvg },
  Meta: { logo: MetaSvg },
  'Z.ai': { logo: ZaiSvg },
};

// A real image made on Easy-Peasy.AI (the "Hero Product Shot" template).
const PRODUCT_PHOTO =
  'https://media.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/c6bea24b-3166-461b-a427-87eabf990a8c.png';

export default function TeamsHero({
  fromPrice,
  models,
  modelCount,
}: {
  /** Lowest per-person monthly price, already formatted. */
  fromPrice: string | null;
  models: HeroModel[];
  modelCount: number;
}) {
  const { t } = useB2bTranslation();

  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto max-w-7xl px-6 pt-12 sm:pt-20 lg:px-8'>
        <a
          href='#pricing'
          className={clsxm(
            styles.enter,
            'group inline-flex max-w-full items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] transition hover:ring-[#0f0f28]/20 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:ring-white/25',
            tone.body,
          )}
        >
          <span className='flex-none rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
            {t('Teams')}
          </span>
          <span className='truncate'>
            {fromPrice
              ? t('Plans from {{price}} per person a month', {
                  price: fromPrice,
                })
              : t('Plans for teams of every size')}
          </span>
          <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5' />
        </a>

        <div className='mt-8 grid gap-x-14 gap-y-8 lg:grid-cols-12 lg:items-end'>
          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              'text-[2.875rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:col-span-7 lg:text-[5.25rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            <Trans
              i18nKey='hero-headline'
              ns='b2b'
              defaultTrans='One AI workspace for your <0>whole team</0>.'
              components={[
                <span
                  key='0'
                  className='text-primary-600 dark:text-primary-400'
                />,
              ]}
            />
          </h1>

          <div
            className={clsxm(styles.enter, 'lg:col-span-5 lg:pb-2')}
            style={delay(160)}
          >
            <p
              className={clsxm(
                'text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              {t(
                'The best AI models, Marky Agent, images, video, voice and AI agents for your customers. Shared across your team, on one plan.',
              )}
            </p>
            <div className='mt-8 flex flex-wrap items-center gap-3'>
              <a href='#pricing' className={buttonPrimary}>
                {t('See team plans')}
                <ArrowGlyph />
              </a>
              <Link href='/contact' className={buttonSecondary}>
                {t('Talk to sales')}
              </Link>
            </div>
            <a
              href='#security'
              className={clsxm(
                'mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px] font-medium transition-colors hover:text-[#10101c] dark:hover:text-white',
                tone.quiet,
              )}
            >
              {COMPLIANCE.map((name) => (
                <span key={name} className='flex items-center gap-1.5'>
                  <ShieldGlyph className='h-4 w-4 text-emerald-600 dark:text-emerald-400' />
                  {name}
                </span>
              ))}
            </a>
          </div>
        </div>
      </div>

      <div className='mx-auto mt-14 max-w-7xl px-3 sm:mt-20 sm:px-6 lg:px-8'>
        <Stage
          variant='indigo'
          halftoneAt='0% 100%'
          className={clsxm(
            styles.enter,
            'px-4 py-8 sm:px-10 sm:py-12 lg:px-12 lg:py-14',
          )}
          style={delay(240)}
        >
          <p className='sr-only'>
            {t(
              'Example: a marketing team asks Marky for a campaign kit and gets a slide deck, a product photo and a voiceover, shared with the whole team.',
            )}
          </p>
          <div
            aria-hidden='true'
            className='relative mx-auto grid max-w-6xl items-start gap-5 lg:grid-cols-12 lg:gap-0'
          >
            <div className='min-w-0 lg:col-span-8 lg:pe-2'>
              <div className={styles.enter} style={delay(360)}>
                <WorkspaceWindow model={models[0]?.title} />
              </div>
            </div>
            {/* Overlaps only the window's side padding, never its content. */}
            <div className='grid min-w-0 gap-5 sm:grid-cols-2 lg:col-span-4 lg:-ms-3 lg:mt-12 lg:grid-cols-1'>
              <div className={styles.enter} style={delay(1300)}>
                <SharedCard />
              </div>
              <div
                className={clsxm(styles.enter, 'lg:ms-8')}
                style={delay(1900)}
              >
                <ModelCard models={models} modelCount={modelCount} />
              </div>
            </div>
          </div>
        </Stage>
      </div>
    </header>
  );
}

function WorkspaceWindow({ model }: { model?: string }) {
  const { t } = useB2bTranslation();
  const steps = [
    t('Read the launch brief'),
    t('Built a 6-slide deck'),
    t('Created the product photo'),
    t('Recorded a 30-second voiceover'),
  ];

  return (
    <div className='overflow-hidden rounded-[22px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <div className='flex items-center gap-3 border-b border-black/[0.06] px-4 py-3.5 sm:px-5 dark:border-white/[0.06]'>
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-xl text-sm font-semibold text-white',
            COMPANY_MARK,
          )}
        >
          K
        </span>
        <div className='min-w-0 flex-1'>
          <p className={clsxm('truncate text-[15px] font-semibold', tone.ink)}>
            {t('Spring launch')}
          </p>
          <p className={clsxm('truncate text-xs', tone.quiet)}>
            {COMPANY} · {t('Marketing')}
          </p>
        </div>
        <AvatarStack people={[TEAM.daniel, TEAM.sofia, TEAM.arjun]} more={4} />
        <span className='hidden items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 sm:flex dark:bg-emerald-400/10 dark:text-emerald-300'>
          <UsersGlyph className='h-3.5 w-3.5' />
          {t('Shared')}
        </span>
      </div>

      <div className='space-y-5 px-4 py-5 text-[14px] leading-relaxed sm:px-5'>
        <div
          className={clsxm(
            styles.bubble,
            styles.bubbleEnd,
            'flex justify-end gap-2.5',
          )}
          style={delay(700)}
        >
          <p
            className={clsxm(
              'max-w-[80%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-4 py-2.5 dark:bg-white/[0.07]',
              tone.ink,
            )}
          >
            {t(
              'Turn the launch brief into a campaign kit: a short deck, a product photo and a 30-second voiceover.',
            )}
          </p>
          <Avatar person={TEAM.maya} className='mt-auto' />
        </div>

        <div className='flex gap-3'>
          <Image
            src='/apple-touch-icon.png'
            alt=''
            width={32}
            height={32}
            className={clsxm(
              styles.enter,
              'h-8 w-8 flex-none rounded-full bg-[#f1f1f5] ring-1 ring-black/[0.06] dark:bg-white/10',
            )}
            style={delay(1000)}
          />
          <div className='min-w-0 flex-1 space-y-3.5'>
            <ul className='space-y-1.5'>
              {steps.map((step, index) => (
                <li
                  key={step}
                  className={clsxm(
                    styles.enter,
                    'flex items-center gap-2 text-[13px]',
                    tone.body,
                  )}
                  style={delay(1100 + index * 260)}
                >
                  <span className='flex h-4 w-4 flex-none items-center justify-center rounded-full bg-emerald-500 text-white'>
                    <CheckGlyph className='h-2.5 w-2.5' />
                  </span>
                  <span className='truncate'>{step}</span>
                </li>
              ))}
            </ul>

            <div
              className={clsxm(styles.enter, 'grid grid-cols-3 gap-2.5')}
              style={delay(2200)}
            >
              <Deliverable label={t('Deck · 6 slides')}>
                <SlideThumb />
              </Deliverable>
              <Deliverable label={t('Product photo')}>
                <Image
                  src={PRODUCT_PHOTO}
                  alt=''
                  fill
                  sizes='(min-width: 1024px) 180px, 30vw'
                  className='object-cover'
                />
              </Deliverable>
              <Deliverable label={t('Voiceover · 0:30')}>
                <VoiceThumb />
              </Deliverable>
            </div>

            <p className={clsxm(styles.enter, tone.ink)} style={delay(2600)}>
              {t(
                'Your campaign kit is ready. Everything follows the Kális brand voice and is shared with Marketing.',
              )}
            </p>
          </div>
        </div>
      </div>

      <div className='px-4 pb-4 sm:px-5'>
        <div className='flex items-center gap-2 rounded-2xl bg-[#f3f3f7] py-2 pe-2 ps-4 dark:bg-white/[0.05]'>
          <span className={clsxm('flex-1 truncate text-sm', tone.quiet)}>
            {t('Ask Marky anything…')}
          </span>
          {model ? (
            <span
              className={clsxm(
                'hidden items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium ring-1 ring-black/[0.06] sm:flex dark:bg-white/10 dark:ring-white/10',
                tone.body,
              )}
            >
              {model}
            </span>
          ) : null}
          <span className='flex h-8 w-8 items-center justify-center rounded-full bg-primary-600 text-white'>
            <ArrowGlyph className='h-3.5 w-3.5 -rotate-90 rtl:rotate-90' />
          </span>
        </div>
      </div>
    </div>
  );
}

function Deliverable({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className='min-w-0'>
      <div className='relative aspect-[4/3] overflow-hidden rounded-xl bg-[#f3f3f7] ring-1 ring-black/[0.06] dark:bg-white/[0.05] dark:ring-white/10'>
        {children}
      </div>
      <p
        className={clsxm('mt-1.5 truncate text-[11px] font-medium', tone.quiet)}
      >
        {label}
      </p>
    </div>
  );
}

/** A drawn title slide in the company's own colours. */
function SlideThumb() {
  const { t } = useB2bTranslation();
  return (
    <div className='absolute inset-0 flex flex-col justify-between bg-[#efe7dc] p-2.5 sm:p-3'>
      <span className='h-1.5 w-6 rounded-full bg-[#1d1c26]/70' />
      <div>
        <p className='text-[10px] font-semibold uppercase leading-none tracking-[0.12em] text-[#8a6d4f] sm:text-[11px]'>
          {COMPANY}
        </p>
        <p className='mt-1 text-[11px] font-semibold leading-tight text-[#1d1c26] sm:text-[13px]'>
          {t('Spring launch')}
        </p>
      </div>
      <span className='absolute bottom-2.5 end-2.5 h-6 w-6 rounded-full bg-[#d9764a]/80 sm:h-8 sm:w-8' />
    </div>
  );
}

const VOICE_BARS = [6, 12, 9, 18, 24, 15, 28, 20, 12, 22, 30, 18, 10, 16, 8];

function VoiceThumb() {
  return (
    <div className='absolute inset-0 flex items-center justify-center gap-[3px] bg-[#1d1c26] px-3'>
      {VOICE_BARS.map((height, index) => (
        <span
          key={index}
          className='w-[3px] flex-none rounded-full bg-primary-300'
          style={{ height: `${height}px` }}
        />
      ))}
      <span className='absolute bottom-2 start-2 flex h-5 w-5 items-center justify-center rounded-full bg-white text-[#1d1c26]'>
        <svg
          viewBox='0 0 20 20'
          fill='currentColor'
          className='h-2.5 w-2.5 rtl:-scale-x-100'
        >
          <path d='M6.3 2.84A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.27l9.34-5.89a1.5 1.5 0 000-2.54L6.3 2.84z' />
        </svg>
      </span>
    </div>
  );
}

function SharedCard() {
  const { t } = useB2bTranslation();
  const items: { person: Teammate; title: string; kind: string }[] = [
    {
      person: TEAM.daniel,
      title: t('Q3 sales playbook'),
      kind: t('Chat'),
    },
    {
      person: TEAM.maya,
      title: t('Kális brand voice'),
      kind: t('Brand voice'),
    },
    {
      person: TEAM.sofia,
      title: t('Customer support agent'),
      kind: t('AI agent'),
    },
    {
      person: TEAM.arjun,
      title: t('Weekly report prompt'),
      kind: t('Prompt'),
    },
  ];

  return (
    <div className='rounded-2xl bg-white p-4 text-start shadow-[0_30px_70px_-25px_rgba(8,6,40,0.7)] ring-1 ring-black/5 dark:bg-[#16151f] dark:ring-white/10'>
      <p
        className={clsxm(
          'flex items-center gap-1.5 px-1 text-xs font-semibold',
          tone.quiet,
        )}
      >
        <UsersGlyph className='h-3.5 w-3.5' />
        {t('Shared with the team')}
      </p>
      <ul className='mt-3 space-y-1'>
        {items.map(({ person, title, kind }) => (
          <li
            key={title}
            className='flex items-center gap-3 rounded-xl px-1 py-1.5'
          >
            <Avatar person={person} />
            <span className='min-w-0 flex-1'>
              <span
                className={clsxm(
                  'block truncate text-[13px] font-semibold leading-tight',
                  tone.ink,
                )}
              >
                {title}
              </span>
              <span className={clsxm('block truncate text-xs', tone.quiet)}>
                {person.name.split(' ')[0]} · {kind}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ModelCard({
  models,
  modelCount,
}: {
  models: HeroModel[];
  modelCount: number;
}) {
  const { t } = useB2bTranslation();
  return (
    <div className='rounded-2xl bg-[#0d0c1f] p-4 text-start text-white shadow-[0_30px_70px_-25px_rgba(8,6,40,0.8)] ring-1 ring-white/10'>
      <p className='px-1 text-xs font-semibold text-white/60'>
        {t('Pick the best model for each task')}
      </p>
      <ul className='mt-3 space-y-1'>
        {models.map(({ provider, title }, index) => {
          const brand = PROVIDER_LOGOS[provider];
          const ModelLogo = brand?.logo;
          return (
            <li
              key={title}
              className={clsxm(
                'flex items-center gap-3 rounded-xl px-2 py-2',
                index === 0 &&
                  'bg-white/[0.08] ring-1 ring-inset ring-white/10',
              )}
            >
              <span className='flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-white'>
                {ModelLogo ? (
                  <ModelLogo className={clsxm('h-4 w-4', brand.className)} />
                ) : null}
              </span>
              <span className='flex-1 truncate text-[13px] font-medium'>
                {title}
              </span>
              {index === 0 && (
                <CheckGlyph className='h-3.5 w-3.5 text-primary-300' />
              )}
            </li>
          );
        })}
      </ul>
      {modelCount > models.length ? (
        <p className='px-2 pb-1 pt-3 text-xs text-white/50'>
          {t('{{count}}+ models in total', { count: modelCount })}
        </p>
      ) : null}
    </div>
  );
}

export function UsersGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox='0 0 20 20'
      fill='currentColor'
      aria-hidden='true'
      className={clsxm('flex-none', className)}
    >
      <path d='M7 8a3 3 0 100-6 3 3 0 000 6zM14.5 9a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM1.615 16.428a1.224 1.224 0 01-.569-1.175 6.002 6.002 0 0111.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 017 18a9.953 9.953 0 01-5.385-1.572zM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 00-1.588-3.755 4.502 4.502 0 015.874 2.636.818.818 0 01-.36.98A7.465 7.465 0 0114.5 16z' />
    </svg>
  );
}
